/**
 * GuestMoreScreen — the «Ещё» tab for a visitor in guest mode (SCRUM-276;
 * spec "Routing and entry points": «Войти / Регистрация» in «Ещё», carrying
 * return_to and the claim intent).
 *
 * A member's «Ещё» is the settings slip (SettingsScreen), whose profile,
 * notification and data bands all need an account. A guest gets the same
 * anatomy with what applies to them: the way in (the conversation follows
 * them — W2.6), what GardEn is (/about, where the landing now lives),
 * language and theme (for most people the only place they change either —
 * see SettingsScreen), and, while a guest conversation exists, «Начать
 * заново» — the contract's "start over" discard — behind a confirmation, so
 * a guest on a shared device can remove their conversation before leaving.
 *
 * DRAFTED STATE (GUARDRAILS rule 10): the prototype has no guest «Ещё»; built
 * from the account Slip primitives and ConfirmDialog exactly as
 * SettingsScreen uses them. Language names are the `settings.langNames`
 * endonyms.
 */
import { Segmented, type SegmentedOption } from "@garden/ui";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ConfirmDialog } from "@/features/account/components/ConfirmDialog";
import {
  DangerZone,
  Slip,
  SlipPage,
  SlipRow,
  SlipSection,
  SlipTitle,
} from "@/features/account/components/Slip";
import { withReturnTo } from "@/features/auth/destination";
import { UI_LANGUAGES, type UiLanguage } from "@/features/auth/schemas";
import { useChatStore } from "@/features/chat/chat-store";
import { discardGuestSession } from "@/features/chat/guest/api";
import { recordClaimIntent, useGuestPointer } from "@/features/chat/guest/pointer";
import { registerSettingsI18n } from "@/features/settings/api";
import { notify } from "@/lib/notify";
import { useUiStore } from "@/lib/ui-store";

export function GuestMoreScreen(): ReactElement {
  registerSettingsI18n();
  const { t } = useTranslation("common");
  const { t: ts } = useTranslation("settings");
  const navigate = useNavigate();
  const pointer = useGuestPointer();
  const hasConversation = pointer !== null && pointer.hasSession;
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const themeValue: "light" | "dark" = theme === "dark" ? "dark" : "light";
  const langOptions: SegmentedOption[] = [
    { value: "ru", label: ts("langNames.ru") },
    { value: "ky", label: ts("langNames.ky") },
    { value: "en", label: ts("langNames.en") },
  ];
  const themeOptions: SegmentedOption[] = [
    { value: "light", label: ts("light") },
    { value: "dark", label: ts("dark") },
  ];

  // The claim intent first (written only while a guest session exists), so
  // whichever account signs in claims this conversation without asking.
  const signIn = (): void => {
    const returnTo = recordClaimIntent();
    navigate(withReturnTo("/login", returnTo));
  };

  const startOver = (): void => {
    setConfirmOpen(false);
    useChatStore.getState().resetAll();
    void discardGuestSession();
    notify.success(t("nav.guestMore.startOverDone"), { id: "guest-start-over" });
    navigate("/", { replace: true });
  };

  return (
    <SlipPage testId="guest-more">
      <SlipTitle title={t("shell.more")} lede={t("nav.guestMore.lede")} />
      <Slip>
        <SlipSection caption={t("nav.guestMore.accountSection")}>
          <SlipRow
            label={t("nav.signInOrUp")}
            hint={hasConversation ? t("nav.guestMore.signInHint") : t("nav.guestMore.signInHintNone")}
            onClick={signIn}
            testId="guest-more-signin"
          />
          <SlipRow
            label={t("nav.guestMore.about")}
            hint={t("nav.guestMore.aboutHint")}
            to="/about"
            testId="guest-more-about"
          />
        </SlipSection>
        <SlipSection caption={ts("appearance")} note={ts("appearanceNote")}>
          <SlipRow label={ts("lang")} value={ts(`langNames.${language}`)}>
            <div data-testid="guest-more-language">
              <Segmented
                full
                options={langOptions}
                value={language}
                onChange={(value) => {
                  if ((UI_LANGUAGES as readonly string[]).includes(value)) {
                    setLanguage(value as UiLanguage);
                  }
                }}
              />
            </div>
          </SlipRow>
          <SlipRow label={ts("theme")} value={ts(themeValue)}>
            <div data-testid="guest-more-theme">
              <Segmented
                full
                options={themeOptions}
                value={themeValue}
                onChange={(value) => {
                  if (value === "light" || value === "dark") setTheme(value);
                }}
              />
            </div>
          </SlipRow>
        </SlipSection>
      </Slip>

      {hasConversation ? (
        <DangerZone title={t("nav.guestMore.dangerTitle")} testId="guest-more-danger">
          <SlipRow
            danger
            label={t("nav.guestMore.startOver")}
            hint={t("nav.guestMore.startOverHint")}
            onClick={() => setConfirmOpen(true)}
            hideChevron
            testId="guest-more-start-over"
          />
        </DangerZone>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={startOver}
        title={t("nav.guestMore.startOverTitle")}
        body={t("nav.guestMore.startOverBody")}
        confirmLabel={t("nav.guestMore.startOverConfirm")}
        cancelLabel={t("nav.guestMore.cancel")}
        closeLabel={t("nav.guestMore.close")}
        testId="guest-more-start-over-dialog"
      />
    </SlipPage>
  );
}

export default GuestMoreScreen;
